import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../Authentication/AuthContext';
import {
  getBookNameByLanguage,
  getLanguageKey,
  getLanguageLabel,
  getNavigationText,
  getTestamentConfig,
  normalizeBookKey,
} from './BookNames';
import {
  getBookChapterCount,
  getChapterVerses,
  startBibleDownloadCache,
} from './DownloadCache';
import './TestamentVerses.css';

const MIN_VERSE_ZOOM = 0.85;
const MAX_VERSE_ZOOM = 2.2;
const DEFAULT_VERSE_ZOOM = 1;

const PREFETCH_FORWARD_CHAPTERS = 5;
const PREFETCH_BACKWARD_CHAPTERS = 2;

const getSelectedLanguageFromContext = (language) => {
  return getLanguageKey(language);
};

const clampZoom = (value) => {
  return Math.min(MAX_VERSE_ZOOM, Math.max(MIN_VERSE_ZOOM, value));
};

const getTouchDistance = (touches) => {
  if (!touches || touches.length < 2) {
    return 0;
  }

  const firstTouch = touches[0];
  const secondTouch = touches[1];

  const deltaX = firstTouch.clientX - secondTouch.clientX;
  const deltaY = firstTouch.clientY - secondTouch.clientY;

  return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
};

const getValidPrefetchedVerses = ({
  locationState,
  testamentType,
  book,
  chapterNumber,
}) => {
  if (!locationState) {
    return null;
  }

  const {
    prefetchedVerses,
    prefetchedBook,
    prefetchedChapter,
    prefetchedTestamentType,
  } = locationState;

  if (
    prefetchedTestamentType !== testamentType ||
    normalizeBookKey(prefetchedBook) !== normalizeBookKey(book) ||
    Number(prefetchedChapter) !== Number(chapterNumber) ||
    !Array.isArray(prefetchedVerses) ||
    prefetchedVerses.length === 0
  ) {
    return null;
  }

  return prefetchedVerses;
};

const getAppLanguageFromLanguageKey = (languageKey) => {
  if (languageKey === 'english') {
    return 'English';
  }

  if (languageKey === 'deutsch') {
    return 'Deutsch';
  }

  return 'Tamil';
};

const ensurePreviousArrow = (label) => {
  const safeLabel = String(label || '').trim();

  if (!safeLabel) {
    return '‹ Previous';
  }

  return safeLabel.includes('‹') ? safeLabel : `‹ ${safeLabel}`;
};

const ensureNextArrow = (label) => {
  const safeLabel = String(label || '').trim();

  if (!safeLabel) {
    return 'Next ›';
  }

  return safeLabel.includes('›') ? safeLabel : `${safeLabel} ›`;
};

function TestamentVerses({ testamentType = 'old' }) {
  const { book: routeBook, chapter: routeChapter } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useContext(AuthContext);

  const config = getTestamentConfig(testamentType);

  const book = normalizeBookKey(routeBook);
  const chapterNumber = Number(routeChapter) || 1;
  const totalChapters = getBookChapterCount(testamentType, book);

  const isMountedRef = useRef(true);
  const activeRequestRef = useRef(0);
  const verseContainerRef = useRef(null);
  const chapterHeaderRef = useRef(null);
  const pinchStartDistanceRef = useRef(null);
  const pinchStartZoomRef = useRef(DEFAULT_VERSE_ZOOM);
  const verseZoomRef = useRef(DEFAULT_VERSE_ZOOM);
  const pageTouchStartYRef = useRef(0);
  const pageTouchLastYRef = useRef(0);
  const bodyScrollYRef = useRef(0);

  const locationState = location.state || null;

  const initialPrefetchedVerses = getValidPrefetchedVerses({
    locationState,
    testamentType,
    book,
    chapterNumber,
  });

  const [verseZoom, setVerseZoom] = useState(DEFAULT_VERSE_ZOOM);

  const [verses, setVerses] = useState(() => {
    if (initialPrefetchedVerses) {
      return initialPrefetchedVerses;
    }

    return [];
  });

  const [selectedLanguages, setSelectedLanguages] = useState([
    getSelectedLanguageFromContext(language),
  ]);

  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(() => {
    return !initialPrefetchedVerses;
  });

  useEffect(() => {
    isMountedRef.current = true;
    startBibleDownloadCache();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    verseZoomRef.current = verseZoom;
  }, [verseZoom]);

  useEffect(() => {
    setSelectedLanguages([getSelectedLanguageFromContext(language)]);
  }, [language]);

  useEffect(() => {
    const updateMobileViewportVariables = () => {
      const visualViewport = window.visualViewport;
      const visualViewportHeight =
        visualViewport && visualViewport.height
          ? visualViewport.height
          : window.innerHeight;

      const bottomBrowserSpace =
        visualViewport && visualViewport.height
          ? Math.max(
              0,
              window.innerHeight -
                visualViewport.height -
                visualViewport.offsetTop
            )
          : 0;

      document.documentElement.style.setProperty(
        '--testament-visual-viewport-height',
        `${Math.round(visualViewportHeight)}px`
      );

      document.documentElement.style.setProperty(
        '--testament-mobile-bottom-space',
        `${Math.round(bottomBrowserSpace)}px`
      );
    };

    updateMobileViewportVariables();

    window.addEventListener('resize', updateMobileViewportVariables);
    window.addEventListener('orientationchange', updateMobileViewportVariables);

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        updateMobileViewportVariables
      );
      window.visualViewport.addEventListener(
        'scroll',
        updateMobileViewportVariables
      );
    }

    return () => {
      window.removeEventListener('resize', updateMobileViewportVariables);
      window.removeEventListener(
        'orientationchange',
        updateMobileViewportVariables
      );

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateMobileViewportVariables
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateMobileViewportVariables
        );
      }

      document.documentElement.style.removeProperty(
        '--testament-visual-viewport-height'
      );
      document.documentElement.style.removeProperty(
        '--testament-mobile-bottom-space'
      );
    };
  }, []);

  useEffect(() => {
    bodyScrollYRef.current = window.scrollY || window.pageYOffset || 0;

    document.documentElement.classList.add('testament-scroll-lock');
    document.body.classList.add('testament-scroll-lock');

    document.body.style.top = `-${bodyScrollYRef.current}px`;

    return () => {
      document.documentElement.classList.remove('testament-scroll-lock');
      document.body.classList.remove('testament-scroll-lock');

      document.body.style.top = '';

      window.scrollTo(0, bodyScrollYRef.current);
    };
  }, []);

  useEffect(() => {
    const stopFixedAreaDrag = (event) => {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      event.preventDefault();
    };

    const headerElements = [
      chapterHeaderRef.current,
      document.querySelector('.top-header-shell'),
      document.querySelector('.top-header'),
      document.querySelector('.contact-info'),
    ].filter(Boolean);

    headerElements.forEach((headerElement) => {
      headerElement.addEventListener('touchmove', stopFixedAreaDrag, {
        passive: false,
      });
    });

    return () => {
      headerElements.forEach((headerElement) => {
        headerElement.removeEventListener('touchmove', stopFixedAreaDrag);
      });
    };
  }, [verses.length, loading, error]);

  useEffect(() => {
    const getScrollableElement = (target) => {
      const verseElement = verseContainerRef.current;

      if (!verseElement || !target) {
        return null;
      }

      if (!verseElement.contains(target)) {
        return null;
      }

      if (
        target.closest &&
        target.closest('.testamentverse-chapter-header')
      ) {
        return null;
      }

      let currentElement = target;

      while (currentElement && currentElement !== document.body) {
        if (currentElement === verseElement) {
          return verseElement;
        }

        const style = window.getComputedStyle(currentElement);
        const isScrollable =
          /(auto|scroll)/.test(style.overflowY) &&
          currentElement.scrollHeight > currentElement.clientHeight + 1;

        if (isScrollable) {
          return currentElement;
        }

        currentElement = currentElement.parentElement;
      }

      return verseElement;
    };

    const preventWindowDrag = () => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    const handleTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      const currentY = event.touches[0].clientY;

      pageTouchStartYRef.current = currentY;
      pageTouchLastYRef.current = currentY;
    };

    const handleTouchMove = (event) => {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      const scrollableElement = getScrollableElement(event.target);

      if (!scrollableElement) {
        event.preventDefault();
        preventWindowDrag();
        return;
      }

      const currentY = event.touches[0].clientY;
      const movementY = currentY - pageTouchLastYRef.current;

      pageTouchLastYRef.current = currentY;

      const scrollTop = scrollableElement.scrollTop;
      const maxScrollTop =
        scrollableElement.scrollHeight - scrollableElement.clientHeight;

      const canActuallyScroll = maxScrollTop > 1;

      if (!canActuallyScroll) {
        event.preventDefault();
        preventWindowDrag();
        return;
      }

      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop >= maxScrollTop - 1;

      if ((isAtTop && movementY > 0) || (isAtBottom && movementY < 0)) {
        event.preventDefault();
        preventWindowDrag();
      }
    };

    const handleWheel = (event) => {
      const scrollableElement = getScrollableElement(event.target);

      if (!scrollableElement) {
        event.preventDefault();
        preventWindowDrag();
        return;
      }

      const maxScrollTop =
        scrollableElement.scrollHeight - scrollableElement.clientHeight;

      const isAtTop = scrollableElement.scrollTop <= 0;
      const isAtBottom = scrollableElement.scrollTop >= maxScrollTop - 1;

      if (
        (isAtTop && event.deltaY < 0) ||
        (isAtBottom && event.deltaY > 0)
      ) {
        event.preventDefault();
        preventWindowDrag();
      }
    };

    const handleGesture = (event) => {
      event.preventDefault();
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
      capture: true,
    });

    document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
      capture: true,
    });

    document.addEventListener('wheel', handleWheel, {
      passive: false,
      capture: true,
    });

    document.addEventListener('gesturestart', handleGesture, {
      passive: false,
      capture: true,
    });

    document.addEventListener('gesturechange', handleGesture, {
      passive: false,
      capture: true,
    });

    document.addEventListener('gestureend', handleGesture, {
      passive: false,
      capture: true,
    });

    window.addEventListener('scroll', preventWindowDrag, {
      passive: true,
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, true);
      document.removeEventListener('touchmove', handleTouchMove, true);
      document.removeEventListener('wheel', handleWheel, true);
      document.removeEventListener('gesturestart', handleGesture, true);
      document.removeEventListener('gesturechange', handleGesture, true);
      document.removeEventListener('gestureend', handleGesture, true);
      window.removeEventListener('scroll', preventWindowDrag);
    };
  }, []);

  const fetchVerses = useCallback(
    async ({
      activeTestamentType = testamentType,
      activeBook,
      activeChapter,
      silent = false,
    }) => {
      if (!activeTestamentType || !activeBook || !activeChapter) {
        return [];
      }

      if (!silent && isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const loadedVerses = await getChapterVerses(
          activeTestamentType,
          activeBook,
          activeChapter
        );

        const safeVerses = Array.isArray(loadedVerses) ? loadedVerses : [];

        if (safeVerses.length > 0) {
          if (!silent && isMountedRef.current) {
            setVerses(safeVerses);
            setLoading(false);
            setError(null);
          }

          return safeVerses;
        }

        if (!silent && isMountedRef.current) {
          setVerses([]);
          setError('Failed to fetch verses');
          setLoading(false);
        }

        return [];
      } catch (requestError) {
        if (!silent && isMountedRef.current) {
          setVerses([]);
          setError(requestError.message || 'Failed to fetch verses');
          setLoading(false);
        }

        return [];
      }
    },
    [testamentType]
  );

  useEffect(() => {
    const currentRequestId = activeRequestRef.current + 1;
    activeRequestRef.current = currentRequestId;

    const statePrefetchedVerses = getValidPrefetchedVerses({
      locationState,
      testamentType,
      book,
      chapterNumber,
    });

    if (statePrefetchedVerses && statePrefetchedVerses.length > 0) {
      setVerses(statePrefetchedVerses);
      setLoading(false);
      setError(null);

      window.requestAnimationFrame(() => {
        if (verseContainerRef.current) {
          verseContainerRef.current.scrollTop = 0;
        }
      });

      return;
    }

    setVerses([]);
    setLoading(true);
    setError(null);

    fetchVerses({
      activeTestamentType: testamentType,
      activeBook: book,
      activeChapter: chapterNumber,
      silent: false,
    }).then((loadedVerses) => {
      if (
        activeRequestRef.current !== currentRequestId ||
        !isMountedRef.current
      ) {
        return;
      }

      if (!loadedVerses || loadedVerses.length === 0) {
        setLoading(false);
      }

      window.requestAnimationFrame(() => {
        if (verseContainerRef.current) {
          verseContainerRef.current.scrollTop = 0;
        }
      });
    });
  }, [book, chapterNumber, fetchVerses, locationState, testamentType]);

  useEffect(() => {
    if (!totalChapters) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const chaptersToPrefetch = [];

      for (let i = 1; i <= PREFETCH_FORWARD_CHAPTERS; i += 1) {
        const nextChapter = chapterNumber + i;

        if (nextChapter <= totalChapters) {
          chaptersToPrefetch.push(nextChapter);
        }
      }

      for (let i = 1; i <= PREFETCH_BACKWARD_CHAPTERS; i += 1) {
        const previousChapter = chapterNumber - i;

        if (previousChapter >= 1) {
          chaptersToPrefetch.push(previousChapter);
        }
      }

      chaptersToPrefetch.forEach((chapterToPrefetch) => {
        fetchVerses({
          activeTestamentType: testamentType,
          activeBook: book,
          activeChapter: chapterToPrefetch,
          silent: true,
        });
      });
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [book, chapterNumber, totalChapters, fetchVerses, testamentType]);

  useEffect(() => {
    const verseElement = verseContainerRef.current;

    if (!verseElement) {
      return undefined;
    }

    const handleNativeTouchStart = (event) => {
      if (event.touches.length === 2) {
        event.preventDefault();
        event.stopPropagation();

        pinchStartDistanceRef.current = getTouchDistance(event.touches);
        pinchStartZoomRef.current = verseZoomRef.current;
      }
    };

    const handleNativeTouchMove = (event) => {
      if (event.touches.length !== 2 || !pinchStartDistanceRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const currentDistance = getTouchDistance(event.touches);

      if (!currentDistance) {
        return;
      }

      const scaleChange = currentDistance / pinchStartDistanceRef.current;
      const nextZoom = clampZoom(pinchStartZoomRef.current * scaleChange);

      setVerseZoom(Number(nextZoom.toFixed(3)));
    };

    const handleNativeTouchEnd = (event) => {
      if (event.touches.length < 2) {
        pinchStartDistanceRef.current = null;
        pinchStartZoomRef.current = verseZoomRef.current;
      }
    };

    const handleNativeWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const direction = event.deltaY > 0 ? -1 : 1;
      const nextZoom = clampZoom(verseZoomRef.current + direction * 0.08);

      setVerseZoom(Number(nextZoom.toFixed(3)));
    };

    const handleNativeGesture = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    verseElement.addEventListener('touchstart', handleNativeTouchStart, {
      passive: false,
    });
    verseElement.addEventListener('touchmove', handleNativeTouchMove, {
      passive: false,
    });
    verseElement.addEventListener('touchend', handleNativeTouchEnd, {
      passive: false,
    });
    verseElement.addEventListener('touchcancel', handleNativeTouchEnd, {
      passive: false,
    });
    verseElement.addEventListener('wheel', handleNativeWheel, {
      passive: false,
    });
    verseElement.addEventListener('gesturestart', handleNativeGesture, {
      passive: false,
    });
    verseElement.addEventListener('gesturechange', handleNativeGesture, {
      passive: false,
    });
    verseElement.addEventListener('gestureend', handleNativeGesture, {
      passive: false,
    });

    return () => {
      verseElement.removeEventListener('touchstart', handleNativeTouchStart);
      verseElement.removeEventListener('touchmove', handleNativeTouchMove);
      verseElement.removeEventListener('touchend', handleNativeTouchEnd);
      verseElement.removeEventListener('touchcancel', handleNativeTouchEnd);
      verseElement.removeEventListener('wheel', handleNativeWheel);
      verseElement.removeEventListener('gesturestart', handleNativeGesture);
      verseElement.removeEventListener('gesturechange', handleNativeGesture);
      verseElement.removeEventListener('gestureend', handleNativeGesture);
    };
  }, []);

  const availableLanguages = useMemo(() => ['tamil', 'english', 'deutsch'], []);

  const orderedLanguages = useMemo(() => {
    const selectedSet = new Set(selectedLanguages);

    return [
      ...selectedLanguages,
      ...availableLanguages.filter((lang) => !selectedSet.has(lang)),
    ];
  }, [availableLanguages, selectedLanguages]);

  const previousChapterTarget = useMemo(() => {
    if (!book || !chapterNumber || chapterNumber <= 1) {
      return null;
    }

    return {
      testamentType,
      routeSegment: config.routeSegment,
      bookKey: book,
      chapterNumber: chapterNumber - 1,
    };
  }, [book, chapterNumber, config.routeSegment, testamentType]);

  const nextChapterTarget = useMemo(() => {
    if (!book || !chapterNumber || !totalChapters) {
      return null;
    }

    if (chapterNumber >= totalChapters) {
      return null;
    }

    return {
      testamentType,
      routeSegment: config.routeSegment,
      bookKey: book,
      chapterNumber: chapterNumber + 1,
    };
  }, [book, chapterNumber, config.routeSegment, testamentType, totalChapters]);

  const prefetchChapterBeforeNavigate = (target) => {
    if (!target) {
      return;
    }

    fetchVerses({
      activeTestamentType: target.testamentType,
      activeBook: target.bookKey,
      activeChapter: target.chapterNumber,
      silent: true,
    });
  };

  const handleLanguageToggle = (selectedLanguage) => {
    setSelectedLanguages((previousLanguages) => {
      if (previousLanguages.includes(selectedLanguage)) {
        if (previousLanguages.length === 1) {
          return previousLanguages;
        }

        return previousLanguages.filter((lang) => lang !== selectedLanguage);
      }

      return [...previousLanguages, selectedLanguage];
    });
  };

  const handleAdjacentChapterClick = (target) => {
    if (!target) {
      return;
    }

    prefetchChapterBeforeNavigate(target);

    navigate(
      `/${target.routeSegment}/chapters/${target.bookKey}/${target.chapterNumber}`
    );
  };

  const handleBibleBookClick = () => {
    navigate(`/bible?testament=${testamentType}`, {
      state: {
        activeTestament: testamentType,
        testamentType,
        fromBooksButton: true,
      },
    });
  };

  const getChapterHeaderText = () => {
    const bookNames = selectedLanguages
      .map((selectedLanguage) =>
        getBookNameByLanguage(
          book,
          getAppLanguageFromLanguageKey(selectedLanguage)
        )
      )
      .filter(Boolean)
      .join(' | ');

    return `${bookNames || getBookNameByLanguage(book, language)} : ${chapterNumber}`;
  };

  const texts = getNavigationText(language);
  const previousButtonText = ensurePreviousArrow(texts.previous);
  const nextButtonText = ensureNextArrow(texts.next);

  if (!totalChapters) {
    return (
      <main className="testament-verses-page">
        <section className="testament-verses-result-container">
          <div className="testament-inline-error">{texts.notFound}</div>
        </section>
      </main>
    );
  }

  return (
    <main className="testament-verses-page">
      <section
        ref={verseContainerRef}
        className="testament-verses-result-container"
        style={{ '--verse-zoom': verseZoom }}
      >
        <div ref={chapterHeaderRef} className="testamentverse-chapter-header">
          <div className="testamentverse-header-left">
            <div className="testamentverse-chapter-header-title">
              {getChapterHeaderText()}
            </div>

            <div className="testamentverse-language-checkboxes">
              {orderedLanguages.map((lang) => (
                <label key={lang}>
                  <input
                    type="checkbox"
                    value={lang}
                    checked={selectedLanguages.includes(lang)}
                    onChange={() => handleLanguageToggle(lang)}
                  />
                  {getLanguageLabel(lang)}
                </label>
              ))}
            </div>
          </div>

          <div className="testamentverse-chapter-nav">
            <button
              type="button"
              className="testamentverse-chapter-nav-button"
              onClick={handleBibleBookClick}
            >
              {texts.books}
            </button>

            <button
              type="button"
              className="testamentverse-chapter-nav-button"
              disabled={!previousChapterTarget}
              onClick={() => handleAdjacentChapterClick(previousChapterTarget)}
            >
              {previousButtonText}
            </button>

            <button
              type="button"
              className="testamentverse-chapter-nav-button"
              disabled={!nextChapterTarget}
              onClick={() => handleAdjacentChapterClick(nextChapterTarget)}
            >
              {nextButtonText}
            </button>
          </div>
        </div>

        {loading && verses.length === 0 && (
          <div className="testament-inline-loading">
            <div className="testament-loading-spinner"></div>
            <p className="testament-spinner-text">{texts.loading}</p>
          </div>
        )}

        {error && verses.length === 0 && (
          <div className="testament-inline-error">{error}</div>
        )}

        {!loading && !error && verses.length > 0 && (
          <div className="testamentverse-results-list">
            {verses.map((verse, index) => {
              const verseNumberText = `${verse?.verse_number || index + 1}. `;

              return (
                <div
                  key={`${book}-${chapterNumber}-${verse?.verse_number || index}`}
                  className="verse-row"
                >
                  {selectedLanguages.map((selectedLanguage, langIndex) => (
                    <p
                      key={`${selectedLanguage}-${verse?.verse_number || index}`}
                      className={`verse-line testamentverse-line-grid ${
                        langIndex > 0 ? 'testamentverse-secondary-line' : ''
                      }`}
                    >
                      {langIndex === 0 ? (
                        <span className="verse-number-inline">
                          {verseNumberText}
                        </span>
                      ) : (
                        <span className="verse-number-spacer">
                          {verseNumberText}
                        </span>
                      )}

                      <span className="testamentverse-verse-text">
                        {verse?.[selectedLanguage] || ''}
                      </span>
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}

export default TestamentVerses;